import { useState, useEffect } from "react";

const APP_URL = typeof window !== 'undefined' ? window.location.origin : 'https://portail.jobotto.fr';
const QR_URL = `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(APP_URL)}&size=200x200&bgcolor=FFFFFF&color=0B1220&qzone=2`;

export default function InstallPage() {
  const [isInstalled, setIsInstalled] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/.test(navigator.userAgent);
  const isStandalone = window.matchMedia("(display-mode: standalone)").matches || (window.navigator as any).standalone;

  useEffect(() => {
    if (isStandalone) setIsInstalled(true);
    const handler = (e: any) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, [isStandalone]);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") setIsInstalled(true);
    setDeferredPrompt(null);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center px-4 py-10 max-w-lg mx-auto">
      <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-5">
        <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <rect x="5" y="2" width="14" height="20" rx="2" ry="2" /><path d="M12 18h.01" />
        </svg>
      </div>

      <h1 className="text-2xl font-bold text-foreground text-center mb-2">Installer le Portail</h1>
      <p className="text-muted-foreground text-center text-sm mb-8 max-w-xs">
        Ajoutez le Portail Partenaires à votre écran d'accueil pour un accès rapide.
      </p>

      {isInstalled ? (
        <div className="flex flex-col items-center gap-3 py-6">
          <div className="w-14 h-14 rounded-full bg-green-500/10 flex items-center justify-center">
            <svg className="w-7 h-7 text-green-500" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg>
          </div>
          <p className="font-semibold text-foreground">Application installée !</p>
          <p className="text-sm text-muted-foreground text-center">Le portail est disponible sur votre écran d'accueil.</p>
        </div>
      ) : (
        <>
          {deferredPrompt && (
            <button onClick={handleInstall} className="w-full mb-6 flex items-center justify-center gap-2 py-3 bg-primary text-primary-foreground rounded-xl font-semibold hover:bg-primary/90 transition-colors">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
              Installer l'application
            </button>
          )}

          {isIOS && (
            <div className="w-full bg-card border border-border rounded-2xl p-5 mb-4">
              <h2 className="font-semibold text-sm text-foreground mb-4">📱 iPhone / iPad</h2>
              <ol className="space-y-3">
                {[
                  'Appuyez sur le bouton Partager en bas de Safari',
                  '"Sur l\'écran d\'accueil"',
                  'Confirmez en appuyant sur "Ajouter"',
                ].map((text, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-muted-foreground">
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary shrink-0">{i + 1}</div>
                    <span className="mt-0.5">{text}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {isAndroid && !deferredPrompt && (
            <div className="w-full bg-card border border-border rounded-2xl p-5 mb-4">
              <h2 className="font-semibold text-sm text-foreground mb-4">🤖 Android</h2>
              <ol className="space-y-3">
                {[
                  'Appuyez sur le menu ⋮ en haut à droite',
                  '"Ajouter à l\'écran d\'accueil"',
                  'Confirmez en appuyant sur "Ajouter"',
                ].map((text, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-muted-foreground">
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary shrink-0">{i + 1}</div>
                    <span className="mt-0.5">{text}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {!isIOS && !isAndroid && (
            <div className="w-full bg-card border border-border rounded-2xl p-5 flex flex-col items-center gap-4">
              <h2 className="font-semibold text-sm text-foreground">Ouvrir sur mobile</h2>
              <img src={QR_URL} alt="QR Code Portail" className="w-48 h-48 rounded-xl" />
              <p className="text-xs text-muted-foreground text-center">Scannez ce code avec votre smartphone</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
